import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiArchive, FiDollarSign, FiInfo, FiLayers, FiTag } from 'react-icons/fi';
import type { IconType } from 'react-icons';

import Modal from '../../../../components/Modal';
import type { Product } from '../../../../types';
import ProductImageThumbnail from './ProductImageThumbnail';
import ProductImagesViewerModal from './ProductImagesViewerModal';
import * as s from './ProductDetailsModal.styles';

interface ProductDetailsModalProps {
  product: Product;
  closeModal: () => void;
}

type ProductKey = keyof Product;

interface DetailField {
  key: ProductKey;
  label: string;
  currency?: boolean;
  percent?: boolean;
}

interface DetailSection {
  title: string;
  icon: IconType;
  fields: DetailField[];
}

function displayValue(value: unknown, field: DetailField): string {
  if (value === null || value === undefined || value === '') return '-';
  if (field.currency) {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value));
  }
  if (field.percent) return `${value}%`;
  return String(value);
}

const ProductDetailsModal = ({ product, closeModal }: ProductDetailsModalProps) => {
  const { t } = useTranslation();
  const [showImagesModal, setShowImagesModal] = useState(false);
  const productImages = product.images?.length
    ? product.images
    : product.image
      ? [product.image]
      : [];

  const sections = useMemo<DetailSection[]>(() => [
    {
      title: t('productDetails.sections.identification'),
      icon: FiTag,
      fields: [
        { key: 'CodigoProduto', label: t('productCode') },
        { key: 'Descricao', label: t('description') },
        { key: 'CodigoEmpresa', label: t('productDetails.fields.company') },
        { key: 'referencia', label: t('productDetails.fields.reference') },
        { key: 'EAN13', label: 'EAN13' },
        { key: 'DUN14', label: 'DUN14' },
        { key: 'TipoProduto', label: t('productDetails.fields.type') },
      ],
    },
    {
      title: t('productDetails.sections.inventory'),
      icon: FiArchive,
      fields: [
        { key: 'QtdEstoque', label: t('productDetails.fields.stock') },
        { key: 'QtdEmbalagem', label: t('productDetails.fields.packageQuantity') },
        { key: 'UnidProduto', label: t('productDetails.fields.unit') },
        { key: 'embalagem', label: t('productDetails.fields.package') },
        { key: 'multiplo', label: t('productDetails.fields.multiple') },
        { key: 'qtdmax', label: t('productDetails.fields.maximumQuantity') },
        { key: 'GrupoEstoque', label: t('productDetails.fields.stockGroup') },
        { key: 'ATIVO', label: t('status') },
        { key: 'CodigoStatus', label: t('productDetails.fields.productStatus') },
      ],
    },
    {
      title: t('productDetails.sections.commercial'),
      icon: FiDollarSign,
      fields: [
        { key: 'PrecoProduto', label: t('productDetails.fields.productPrice'), currency: true },
        { key: 'custo', label: t('productDetails.fields.cost'), currency: true },
        { key: 'MaxAcresc', label: t('productDetails.fields.maximumIncrease'), percent: true },
        { key: 'DescPermitido', label: t('productDetails.fields.allowedDiscount'), percent: true },
        { key: 'PercFlex', label: t('productDetails.fields.flexiblePercent'), percent: true },
        { key: 'Comissao', label: t('productDetails.fields.commission'), percent: true },
        { key: 'MCProduto', label: t('productDetails.fields.margin'), percent: true },
        { key: 'rentabilidadeBase', label: t('productDetails.fields.baseProfitability'), percent: true },
        { key: 'IPI', label: 'IPI', percent: true },
        { key: 'pis', label: 'PIS', percent: true },
        { key: 'cofins', label: 'COFINS', percent: true },
      ],
    },
    {
      title: t('productDetails.sections.classification'),
      icon: FiLayers,
      fields: [
        { key: 'codigoCategoria', label: t('productDetails.fields.category') },
        { key: 'codigoFornecedor', label: t('productDetails.fields.supplier') },
        { key: 'codigoGrupoProduto', label: t('productDetails.fields.productGroup') },
        { key: 'CodigoCategoriaComercial', label: t('productDetails.fields.commercialCategory') },
        { key: 'GrupoProduto1', label: 'Grupo de produto 1' },
        { key: 'GrupoProduto2', label: 'Grupo de produto 2' },
        { key: 'GrupoProduto3', label: 'Grupo de produto 3' },
        { key: 'GrupoComercial', label: t('productDetails.fields.commercialGroup') },
        { key: 'CodigoSegmento', label: t('productDetails.fields.segment') },
        { key: 'Sabor', label: t('productDetails.fields.flavor') },
        { key: 'cor', label: t('productDetails.fields.color') },
      ],
    },
    {
      title: t('productDetails.sections.technical'),
      icon: FiInfo,
      fields: [
        { key: 'PesoBruto', label: t('productDetails.fields.grossWeight') },
        { key: 'Volume', label: t('productDetails.fields.volume') },
        { key: 'cubagem', label: t('productDetails.fields.cubicVolume') },
        { key: 'FlagValorFixo', label: 'FlagValorFixo' },
        { key: 'FlagSitEspecial', label: 'FlagSitEspecial' },
        { key: 'ValidaDescontoPeso', label: 'ValidaDescontoPeso' },
        { key: 'ExcecaoPedidoMinimo', label: 'ExcecaoPedidoMinimo' },
        { key: 'ValidaJurosCondPgto', label: 'ValidaJurosCondPgto' },
        { key: 'TempoValidade', label: t('productDetails.fields.expiration') },
        { key: 'Obs', label: t('productDetails.fields.notes') },
        { key: 'legenda', label: t('productDetails.fields.legend') },
        { key: 'DataAtualizacao', label: t('productDetails.fields.updatedAt') },
      ],
    },
  ], [t]);

  return (
    <>
      <Modal
        title={`${t('productDetails.title')}: ${product.Descricao}`}
        showModal
        closeModal={closeModal}
        onlyCloseButton
        maximized
        bodyStyle={{ maxHeight: 'calc(100vh - 170px)', overflowY: 'auto' }}
      >
        <s.DetailsContent>
          <s.ProductHero>
            <ProductImageThumbnail
              image={productImages[0]}
              imageCount={productImages.length}
              alt={product.Descricao}
              size="preview"
              onClick={() => setShowImagesModal(true)}
            />
            <s.HeroCopy>
              <s.HeroEyebrow>{t('productDetails.title')}</s.HeroEyebrow>
              <s.HeroTitle>{product.Descricao}</s.HeroTitle>
              <s.HeroCode>{`${t('productCode')}: ${product.CodigoProduto}`}</s.HeroCode>
            </s.HeroCopy>
            <s.StatusBadge $active={product.ATIVO?.toUpperCase() === 'S'}>
              {product.ATIVO?.toUpperCase() === 'S' ? t('active') : t('inactive')}
            </s.StatusBadge>
          </s.ProductHero>

          {sections.map((section) => {
            const SectionIcon = section.icon;

            return (
              <s.DetailsSection key={section.title}>
                <s.SectionHeader>
                  <s.SectionAccent />
                  <s.SectionIcon>
                    <SectionIcon size={17} aria-hidden="true" />
                  </s.SectionIcon>
                  <s.SectionTitle>{section.title}</s.SectionTitle>
                </s.SectionHeader>
                <s.FieldsGrid>
                  {section.fields.map((field) => {
                    const isActive = product.ATIVO?.toUpperCase() === 'S';
                    const value = field.key === 'ATIVO'
                      ? (isActive ? t('active') : t('inactive'))
                      : displayValue(product[field.key], field);

                    return (
                      <s.DetailFieldCard key={field.key}>
                        <s.FieldLabel>{field.label}</s.FieldLabel>
                        {field.key === 'ATIVO' ? (
                          <s.FieldStatusValue $active={isActive}>{value}</s.FieldStatusValue>
                        ) : (
                          <s.FieldValue>{value}</s.FieldValue>
                        )}
                      </s.DetailFieldCard>
                    );
                  })}
                </s.FieldsGrid>
              </s.DetailsSection>
            );
          })}
        </s.DetailsContent>
      </Modal>
      {showImagesModal && (
        <ProductImagesViewerModal
          product={product}
          closeModal={() => setShowImagesModal(false)}
        />
      )}
    </>
  );
};

export default ProductDetailsModal;
